import { toast } from "sonner";

// OpenRoute API key (this would ideally be secured in a backend environment)
const API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;

// Debug: Check if API key is loaded
console.log("API Key loaded:", API_KEY ? "Yes" : "No");
console.log("API Key length:", API_KEY ? API_KEY.length : 0);
if (!API_KEY) {
  console.error("OpenRouter API key is missing. Please check your .env file and ensure VITE_OPENROUTER_API_KEY is set.");
}

const MODEL = "google/gemini-2.5-flash-preview-05-20";

export interface AnalysisResult {
  explanation: string;
  type: string;
  confidence: number;
  suggestions: string[];
  sourceAnalysis?: {
    [key: string]: any;
  };
}

export interface AnalysisRequest {
  text?: string;
  url?: string;
  image?: string; // base64
  customPrompt?: string; // Optional: a specific prompt for the AI
}

export async function analyzeContent(request: AnalysisRequest): Promise<AnalysisResult> {
  try {
    if (!API_KEY) {
      throw new Error("OpenRouter API key is not configured. Please check your environment variables.");
    }

    // Construct the prompt based on what's provided or use customPrompt
    let content = "";
    let prompt = request.customPrompt || `Analyze the following content for potential misinformation or disinformation.\n    Provide a detailed analysis that includes:\n    1. Whether the content contains misinformation, disinformation, or is factually accurate\n    2. Your confidence level in this assessment (as a percentage)\n    3. An explanation of your reasoning\n    4. Suggestions for how readers should approach this content\n    5. For textual content, analyze the source credibility if possible\n    \n    Format your response as JSON with the following structure:\n    {\n      \"type\": \"misinformation\" | \"factual\" | \"misleading\" | \"uncertain\",\n      \"confidence\": number (0-100),\n      \"explanation\": \"detailed explanation\",\n      \"suggestions\": [\"suggestion1\", \"suggestion2\", ...],\n      \"sourceAnalysis\": {\n        \"credibility\": number (0-100),\n        \"factors\": [\n          {\"name\": \"factor name\", \"score\": number (0-100), \"explanation\": \"explanation\"}\n        ]\n      }\n    }\n    \n    IMPORTANT: Return only the JSON without any markdown formatting, code blocks, or additional text.\n    \n    Content to analyze:\n\n`;
    
    if (request.text) {
      content = request.text;
      prompt += `Text content: ${content}\n\n`;
    }
    
    if (request.url) {
      prompt += `URL: ${request.url}\n\n`;
      // If we have just a URL, set content to the URL for the result display
      if (!content) content = request.url;
    }
    
    if (request.image) {
      // For image analysis, we need to use a multimodal prompt
      prompt += `I'm sharing an image with you. Please analyze this image for potential misinformation, manipulated content, or misleading elements. Consider visual cues, text overlays, and any signs of digital manipulation.\n\n`;
      
      // If this is the only content, set a placeholder for the result display
      if (!content) content = "Uploaded image";
    }

    // Prepare the request body
    const requestBody: any = {
      model: MODEL,
      messages: [
        {
          role: "user",
          content: request.image ? [
            { type: "text", text: prompt },
            { type: "image_url", image_url: { url: request.image } }
          ] : prompt
        }
      ],
      response_format: { type: "json_object" }
    };
    
    console.log("Sending analysis request to API");
    
    const finalRequestBody = {
      ...requestBody,
      max_tokens: 1500, 
      temperature: 0.5, 
      stream: false 
    };
    console.log("Request body for analyzeContent:", JSON.stringify(finalRequestBody, null, 2));

    // Send request to OpenRoute API
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_KEY}`,
        "HTTP-Referer": import.meta.env.PROD ? "https://truth-nexus-globe-ai.vercel.app" : window.location.origin,
        "X-Title": "Truth Nexus AI"
      },
      body: JSON.stringify(finalRequestBody)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error("API Error response:", errorData);
      if (response.status === 401) {
        throw new Error("Authentication failed. Please check your OpenRouter API key.");
      }
      const specificApiErrorMessage = errorData.error?.message || 
                                    errorData.error?.detail || 
                                    errorData.message || 
                                    (typeof errorData.error === 'string' ? errorData.error : 'Failed to analyze content');
      throw new Error(`API Error: ${specificApiErrorMessage}`);
    }
    
    const data = await response.json();
    console.log("Received API response:", data);
    
    // Declare cleanedContent outside the try block
    let cleanedContent = "";

    // If the API returned an error, show it to the user
    if (data.error) {
      console.error("OpenRouter API error:", data.error);
      return {
        explanation: data.error.message || "An error occurred with the AI model. Please try again later.",
        type: "uncertain",
        confidence: 0,
        suggestions: ["Try again or use different content."],
        sourceAnalysis: undefined
      };
    }
    
    // Defensive: check for missing or malformed AI response
    if (!data.choices || !Array.isArray(data.choices) || !data.choices[0] || !data.choices[0].message || typeof data.choices[0].message.content !== 'string') {
      console.error("No valid AI response content", data);
      return {
        explanation: "No content available from AI model. Please try again later.",
        type: "uncertain",
        confidence: 0,
        suggestions: ["Try again or use different content."],
        sourceAnalysis: undefined
      };
    }
    
    // Parse the response - fix for JSON parsing issue
    try {
      let aiResponse;
      const rawContent = data.choices[0].message.content;

      // Remove any markdown code block formatting (```json and ```) if present
      const jsonBlockMatch = rawContent.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonBlockMatch && jsonBlockMatch[1]) {
        cleanedContent = jsonBlockMatch[1].trim();
      } else {
         // If no markdown block, just trim whitespace from the raw content
         cleanedContent = rawContent.trim();
      }

      console.log("Cleaned content (pre-parse):", cleanedContent);

      // Attempt to parse the cleaned content as JSON
      aiResponse = JSON.parse(cleanedContent);
      console.log("Parsed AI response:", aiResponse);
      
      return {
        explanation: aiResponse.explanation || "No explanation provided",
        type: aiResponse.type || "uncertain",
        confidence: aiResponse.confidence || 0,
        suggestions: aiResponse.suggestions || [],
        sourceAnalysis: aiResponse.sourceAnalysis
      };
    } catch (parseError) {
      console.error("Error parsing AI response:", parseError, data?.choices?.[0]?.message?.content || "No content available", "\nCleaned content (at time of parse error) was:", cleanedContent);
      // If parsing fails, create a fallback response
      return {
        explanation: "Unable to parse AI response. The model did not return valid JSON.",
        type: "uncertain",
        confidence: 0,
        suggestions: ["Try reformatting or simplifying your content and try again."],
        sourceAnalysis: undefined
      };
    }
  } catch (error) {
    console.error("Error analyzing content:", error);
    toast.error("Failed to analyze content. Please try again later.");
    
    return {
      explanation: "An error occurred while analyzing the content.",
      type: "uncertain",
      confidence: 0,
      suggestions: ["Please try again later."],
      sourceAnalysis: undefined
    };
  }
}

export async function analyzeSource(url: string): Promise<{
  credibility: number;
  factors: { name: string; score: number; explanation: string }[];
  summary: string;
}> {
  try {
    if (!API_KEY) {
      throw new Error("OpenRouter API key is not configured. Please check your environment variables.");
    }

    // Create prompt for source analysis
    const prompt = `Analyze the credibility of the following source/website. 
    Consider factors like:
    1. History of factual reporting
    2. Transparency about ownership and funding
    3. Editorial policies and standards
    4. Political bias or agenda
    5. Use of citation and references
    
    URL: ${url}
    
    Format your response as JSON with the following structure:\n    {\n      \"credibility\": number (0-100),\n      \"summary\": \"brief overall assessment\",\n      \"factors\": [\n        {\"name\": \"factor name\", \"score\": number (0-100), \"explanation\": \"explanation\"}\n      ]\n    }`;
    
    console.log("Sending source analysis request to API");
    
    // Send request to OpenRoute API
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_KEY}`,
        "HTTP-Referer": import.meta.env.PROD ? "https://truth-nexus-globe-ai.vercel.app" : window.location.origin,
        "X-Title": "Truth Nexus AI"
      },
      body: JSON.stringify({
        model: MODEL, // Use the consistent MODEL constant
        messages: [
          {
            role: "user",
            content: prompt
          }
        ],
        response_format: { type: "json_object" }
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error("API Error response:", errorData);
      if (response.status === 401) {
        throw new Error("Authentication failed. Please check your OpenRouter API key.");
      }
      const specificApiErrorMessage = errorData.error?.message || 
                                    errorData.error?.detail || 
                                    errorData.message || 
                                    (typeof errorData.error === 'string' ? errorData.error : 'Failed to analyze source');
      throw new Error(`API Error: ${specificApiErrorMessage}`);
    }
    
    const data = await response.json();
    console.log("Received API response for source analysis:", data);
    
    // Declare cleanedContent outside the try block
    let cleanedContent = "";

    // Parse the response
    try {
      // Handle potential JSON formatting in the response
      const rawSourceContent = data.choices[0].message.content;
      
      const jsonBlockMatch = rawSourceContent.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonBlockMatch && jsonBlockMatch[1]) {
        cleanedContent = jsonBlockMatch[1].trim();
      } else {
         // If no markdown block, just trim whitespace from the raw content
         cleanedContent = rawSourceContent.trim();
      }
      
      console.log("Cleaned source analysis content (pre-parse):", cleanedContent);

      // The model is instructed to return a JSON object via response_format,
      // so 'cleanedContent' should be a string that is directly parsable.
      const aiResponse = JSON.parse(cleanedContent);
      console.log("Parsed source analysis response:", aiResponse);
      
      return {
        credibility: aiResponse.credibility || 0,
        summary: aiResponse.summary || "No summary provided",
        factors: aiResponse.factors || []
      };
    } catch (parseError) {
      console.error("Error parsing source analysis response:", parseError, "\nRaw source content was:", data?.choices?.[0]?.message?.content, "\nCleaned source content (at time of parse error) was:", cleanedContent);
      throw new Error("Failed to parse AI response for source analysis");
    }
  } catch (error) {
    console.error("Error analyzing source:", error);
    toast.error("Failed to analyze source. Please try again later.");
    
    // Return a meaningful error response that can be displayed to the user
    return {
      credibility: 0,
      summary: "An error occurred while analyzing the source. Please check your internet connection and try again.",
      factors: [
        {
          name: "Error",
          score: 0,
          explanation: "There was a technical problem with the analysis. This could be due to API limits, network issues, or an invalid URL format."
        }
      ]
    };
  }
}